import type {
  GradeProgramOption,
  TuitionBenchmarkStatus,
  TuitionFeeBenchmarkDistribution,
  TuitionFeeBenchmarkFilterParams,
  TuitionFeeBenchmarkMatrixCell,
  TuitionFeeBenchmarkResult,
  TuitionFeeBenchmarkSchoolRow,
  TuitionFeeBenchmarkSummary,
} from './tuitionFeeBenchmark.types';

/** Schools within ±NEAR_REGIONAL_THRESHOLD % of regional benchmark are "near". */
export const NEAR_REGIONAL_THRESHOLD_PCT = 10;

const MOCK_GOVERNORATES: { id: number; name: string; cities: { name: string; areas: string[] }[] }[] = [
  {
    id: 8,
    name: 'Mount Lebanon',
    cities: [
      { name: 'Jounieh', areas: ['Kaslik', 'Haret Sakher'] },
      { name: 'Baabda', areas: ['Hazmiyeh', 'Chiyah'] },
      { name: 'Aley', areas: ['Bhamdoun', 'Souk El Gharb'] },
    ],
  },
  {
    id: 9,
    name: 'Nabatieh',
    cities: [
      { name: 'Nabatieh', areas: ['Central', 'Habbouch'] },
      { name: 'Marjayoun', areas: ['Khiam', 'Qlayaa'] },
    ],
  },
  {
    id: 10,
    name: 'South',
    cities: [
      { name: 'Saida', areas: ['Old City', 'Abra'] },
      { name: 'Tyre', areas: ['Bourj El Shamali', 'Qana'] },
    ],
  },
  {
    id: 11,
    name: 'Beirut',
    cities: [
      { name: 'Ras Beirut', areas: ['Hamra', 'Ain El Mreisseh'] },
      { name: 'Achrafieh', areas: ['Sassine', 'Gemmayze'] },
      { name: 'Mazraa', areas: ['Tarik Jdideh', 'Bir Hassan'] },
    ],
  },
  {
    id: 12,
    name: 'Bekaa',
    cities: [
      { name: 'Zahle', areas: ['Maallaka', 'Chtaura'] },
      { name: 'West Bekaa', areas: ['Jeb Jennine', 'Machghara'] },
    ],
  },
  {
    id: 13,
    name: 'North',
    cities: [
      { name: 'Tripoli', areas: ['Mina', 'El Qobbeh'] },
      { name: 'Koura', areas: ['Amioun', 'Chekka'] },
    ],
  },
  {
    id: 14,
    name: 'Baalbek-Hermel',
    cities: [
      { name: 'Baalbek', areas: ['Douris', 'Ras Baalbek'] },
      { name: 'Hermel', areas: ['Al Qasr', 'Chouaghir'] },
    ],
  },
];

/** Program ids aligned with GET /api/app/pick-list/pick-list-by-code-select-item?Code=Programs */
export const MOCK_PROGRAMS: GradeProgramOption[] = [
  { id: 'efce5655-7c59-4202-ab05-1c2e6cf7da79', title: 'منهاج لبناني' },
  { id: '5459627e-bb1c-4a43-9f52-9d88b3e7aa12', title: 'منهاج اجنبي' },
  { id: 'bdd3605b-08eb-460f-ad65-b5fca1ca2374', title: 'البكالوريا الفرنسية' },
  { id: '4bbff27d-a82f-406a-b11f-c4269e257e67', title: 'بكالوريا الدولية' },
  { id: '0407435f-e409-41e1-8d63-e03a01d90e29', title: 'AP' },
  { id: 'd31f6673-aa81-49e7-bc2c-f195d3a86c51', title: 'برامج وصفوف أخرى' },
];

const SCHOOL_PREFIXES = [
  'Al-Noor', 'Al-Amal', 'Al-Hikma', 'Saint Joseph', 'Modern Learning', 'Future Leaders',
  'Beacon', 'Horizon', 'Al-Mustaqbal', 'Les Frères', 'Notre Dame', 'Al-Rawda',
  'Excellence', 'Al-Ittihad', 'Wisdom', 'Al-Najah', 'Bright Minds', 'Al-Fajr',
];

interface RawSchoolGradeRow {
  schoolId: string;
  schoolCode: string;
  schoolName: string;
  year: number;
  governorateId: number;
  governorateName: string;
  cityName: string;
  areaName: string;
  gradeProgramId: string;
  gradeProgramTitle: string;
  studentsInGrade: number;
  tuitionPerStudentUsd: number;
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function programBaseTuition(programId: string): number {
  const bases: Record<string, number> = {
    'efce5655-7c59-4202-ab05-1c2e6cf7da79': 2800,
    '5459627e-bb1c-4a43-9f52-9d88b3e7aa12': 4200,
    'bdd3605b-08eb-460f-ad65-b5fca1ca2374': 5100,
    '4bbff27d-a82f-406a-b11f-c4269e257e67': 5400,
    '0407435f-e409-41e1-8d63-e03a01d90e29': 5900,
    'd31f6673-aa81-49e7-bc2c-f195d3a86c51': 3200,
  };
  return bases[programId] ?? 3000;
}

function govTuitionMultiplier(govId: number): number {
  const m: Record<number, number> = {
    11: 1.18, 8: 1.08, 13: 0.92, 9: 0.88, 10: 0.85, 12: 0.82, 14: 0.78,
  };
  return m[govId] ?? 1;
}

function buildMockCatalogue(): RawSchoolGradeRow[] {
  const rows: RawSchoolGradeRow[] = [];
  let schoolIndex = 0;

  for (const gov of MOCK_GOVERNORATES) {
    for (const city of gov.cities) {
      for (let s = 0; s < 4; s += 1) {
        schoolIndex += 1;
        const rand = seededRandom(schoolIndex * 997 + gov.id);
        const prefix = SCHOOL_PREFIXES[schoolIndex % SCHOOL_PREFIXES.length];
        const area = city.areas[s % city.areas.length];
        const schoolId = `mock-tfb-${schoolIndex.toString(16).padStart(8, '0')}`;
        const schoolCode = String(2000 + schoolIndex);
        const schoolName = `${prefix} — ${city.name}`;

        const program = MOCK_PROGRAMS[schoolIndex % MOCK_PROGRAMS.length];
        const base = programBaseTuition(program.id) * govTuitionMultiplier(gov.id);
        const spread = 0.72 + rand() * 0.56;
        const tuition = Math.round(base * spread);
        const students = Math.max(40, Math.round(120 + rand() * 380));

        rows.push({
          schoolId,
          schoolCode,
          schoolName,
          year: 2025,
          governorateId: gov.id,
          governorateName: gov.name,
          cityName: city.name,
          areaName: area,
          gradeProgramId: program.id,
          gradeProgramTitle: program.title,
          studentsInGrade: students,
          tuitionPerStudentUsd: tuition,
        });
      }
    }
  }

  return rows;
}

const MOCK_CATALOGUE = buildMockCatalogue();

function weightedMean(
  items: { value: number; weight: number }[]
): number {
  const totalWeight = items.reduce((s, i) => s + i.weight, 0);
  if (totalWeight <= 0) return 0;
  return items.reduce((s, i) => s + i.value * i.weight, 0) / totalWeight;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

function buildDistribution(values: number[]): TuitionFeeBenchmarkDistribution {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    min: sorted[0] ?? 0,
    p25: percentile(sorted, 0.25),
    median: percentile(sorted, 0.5),
    p75: percentile(sorted, 0.75),
    max: sorted[sorted.length - 1] ?? 0,
  };
}

function classifyStatus(varianceRegionalPct: number): TuitionBenchmarkStatus {
  if (Math.abs(varianceRegionalPct) <= NEAR_REGIONAL_THRESHOLD_PCT) return 'near';
  return varianceRegionalPct > 0 ? 'above' : 'below';
}

function variancePct(school: number, benchmark: number): number {
  if (!Number.isFinite(benchmark) || benchmark <= 0) return 0;
  return ((school - benchmark) / benchmark) * 100;
}

function matchesRegion(
  row: RawSchoolGradeRow,
  params: TuitionFeeBenchmarkFilterParams
): boolean {
  if (params.governorateId != null && row.governorateId !== params.governorateId) {
    return false;
  }
  if (params.cityName?.trim()) {
    const needle = params.cityName.trim().toLowerCase();
    if (!row.cityName.toLowerCase().includes(needle) && !needle.includes(row.cityName.toLowerCase())) {
      return false;
    }
  }
  if (params.areaName?.trim()) {
    const needle = params.areaName.trim().toLowerCase();
    if (!row.areaName.toLowerCase().includes(needle) && !needle.includes(row.areaName.toLowerCase())) {
      return false;
    }
  }
  return true;
}

function buildRegionalMatrix(
  nationalByGrade: Map<string, number>,
  scopeRows: RawSchoolGradeRow[]
): TuitionFeeBenchmarkMatrixCell[] {
  const cells: TuitionFeeBenchmarkMatrixCell[] = [];
  const regionKey = (r: RawSchoolGradeRow) =>
    `${r.governorateId}|${r.governorateName}`;

  const groups = new Map<string, RawSchoolGradeRow[]>();
  for (const row of scopeRows) {
    const key = regionKey(row);
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }

  for (const [key, groupRows] of groups) {
    const [regionId, regionName] = key.split('|');
    const byGrade = new Map<string, RawSchoolGradeRow[]>();
    for (const r of groupRows) {
      const g = byGrade.get(r.gradeProgramId) ?? [];
      g.push(r);
      byGrade.set(r.gradeProgramId, g);
    }

    for (const [gradeId, gradeRows] of byGrade) {
      const avg = weightedMean(
        gradeRows.map((r) => ({ value: r.tuitionPerStudentUsd, weight: r.studentsInGrade }))
      );
      const national = nationalByGrade.get(gradeId) ?? avg;
      cells.push({
        regionId,
        regionName,
        gradeProgramId: gradeId,
        gradeProgramTitle: gradeRows[0]?.gradeProgramTitle ?? gradeId,
        avgTuitionUsd: Math.round(avg),
        studentsCount: gradeRows.reduce((s, r) => s + r.studentsInGrade, 0),
        schoolsCount: new Set(gradeRows.map((r) => r.schoolId)).size,
        varianceVsNationalPct: Math.round(variancePct(avg, national) * 10) / 10,
      });
    }
  }

  return cells.sort((a, b) => a.regionName.localeCompare(b.regionName) || a.gradeProgramTitle.localeCompare(b.gradeProgramTitle));
}

export function filterTuitionFeeBenchmark(
  params: TuitionFeeBenchmarkFilterParams
): TuitionFeeBenchmarkResult {
  const yearNum = Number.parseInt(params.year.trim(), 10);
  const gradeId = params.gradeProgramId.trim();

  const gradeRowsAllYears = MOCK_CATALOGUE.filter(
    (r) => r.gradeProgramId === gradeId && (!Number.isFinite(yearNum) || r.year === yearNum || yearNum === 2024 || yearNum === 2025 || yearNum === 2026)
  );

  const nationalBenchmarkUsd = Math.round(
    weightedMean(
      gradeRowsAllYears.map((r) => ({
        value: r.tuitionPerStudentUsd,
        weight: r.studentsInGrade,
      }))
    )
  );

  const regionalPool = gradeRowsAllYears.filter((r) => matchesRegion(r, params));
  const regionalBenchmarkUsd =
    params.governorateId != null
      ? Math.round(
          weightedMean(
            regionalPool.map((r) => ({
              value: r.tuitionPerStudentUsd,
              weight: r.studentsInGrade,
            }))
          )
        )
      : null;

  const scopeRows = regionalPool;
  const effectiveRegional = regionalBenchmarkUsd ?? nationalBenchmarkUsd;

  const rows: TuitionFeeBenchmarkSchoolRow[] = scopeRows.map((r) => {
    const vReg = Math.round(variancePct(r.tuitionPerStudentUsd, effectiveRegional) * 10) / 10;
    const vNat = Math.round(variancePct(r.tuitionPerStudentUsd, nationalBenchmarkUsd) * 10) / 10;
    return {
      schoolId: r.schoolId,
      schoolCode: r.schoolCode,
      schoolName: r.schoolName,
      governorateId: r.governorateId,
      governorateName: r.governorateName,
      cityName: r.cityName,
      areaName: r.areaName,
      gradeProgramId: r.gradeProgramId,
      gradeProgramTitle: r.gradeProgramTitle,
      studentsInGrade: r.studentsInGrade,
      tuitionPerStudentUsd: r.tuitionPerStudentUsd,
      regionalBenchmarkUsd: effectiveRegional,
      nationalBenchmarkUsd,
      varianceRegionalPct: vReg,
      varianceNationalPct: vNat,
      benchmarkStatus: classifyStatus(vReg),
    };
  });

  const aboveRegionalCount = rows.filter((r) => r.benchmarkStatus === 'above').length;
  const belowRegionalCount = rows.filter((r) => r.benchmarkStatus === 'below').length;
  const nearRegionalCount = rows.filter((r) => r.benchmarkStatus === 'near').length;

  const summary: TuitionFeeBenchmarkSummary = {
    nationalBenchmarkUsd,
    regionalBenchmarkUsd,
    schoolCount: rows.length,
    aboveRegionalCount,
    belowRegionalCount,
    nearRegionalCount,
  };

  const distribution = buildDistribution(rows.map((r) => r.tuitionPerStudentUsd));

  const nationalByProgram = new Map<string, number>();
  for (const p of MOCK_PROGRAMS) {
    const allForProgram = MOCK_CATALOGUE.filter((r) => r.gradeProgramId === p.id);
    nationalByProgram.set(
      p.id,
      weightedMean(allForProgram.map((r) => ({ value: r.tuitionPerStudentUsd, weight: r.studentsInGrade })))
    );
  }

  const matrixScope = params.governorateId != null
    ? MOCK_CATALOGUE.filter((r) => r.governorateId === params.governorateId)
    : MOCK_CATALOGUE;

  const regionalMatrix = buildRegionalMatrix(nationalByProgram, matrixScope);

  return {
    summary,
    distribution,
    rows,
    regionalMatrix,
  };
}

export function statusLabel(status: TuitionBenchmarkStatus): string {
  if (status === 'above') return 'Above';
  if (status === 'below') return 'Below';
  return 'Near';
}
